import React, { useState } from 'react'
import { CFormLabel, CFormInput, CBadge } from '@coreui/react'
import { EditIcon, X, GripVertical } from 'lucide-react'

const RiskFactorDetails = ({
  keywords,
  setRiskIndexUpdate,
  setKeywords,
  getData,
  label = 'Keywords',
  editMode = false,
  onReorder,
}) => {
  const [textInput, setTextInput] = useState('')
  const [editIndex, setEditIndex] = useState(null)
  const [draggedIndex, setDraggedIndex] = useState(null)

  const handleInputChange = (event) => {
    setTextInput(event.target.value)
  }

  const handleKeyPress = async (event) => {
    if (event.key !== 'Enter') return

    const value = textInput.trim()

    if (!value) return

    if (editIndex !== null) {
      const updatedKeywords = [...keywords]

      updatedKeywords[editIndex] = value

      setKeywords(updatedKeywords)
      setEditIndex(null)
      setRiskIndexUpdate(null)
    } else {
      const newKeywords = value
        .split(',')
        .map((keyword) => keyword.trim())
        .filter(Boolean)

      setKeywords([...keywords, ...newKeywords])
    }

    setTextInput('')
  }

  const handleEditKeyword = (index) => {
    if (!keywords[index]?.trim()) return

    setTextInput(keywords[index])
    setEditIndex(index)
    setRiskIndexUpdate(index)
  }

  const handleRemoveKeyword = (index) => {
    setKeywords(keywords.filter((_, i) => i !== index))

    if (editIndex === index) {
      setEditIndex(null)
      setTextInput('')
      setRiskIndexUpdate(null)
    }
  }

  const handleDragStart = (event, index) => {
    setDraggedIndex(index)

    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', index)
  }

  const handleDragOver = (event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (event, dropIndex) => {
    event.preventDefault()

    const dragIndex = Number(
      event.dataTransfer.getData('text/plain')
    )

    if (
      Number.isNaN(dragIndex) ||
      dragIndex === dropIndex ||
      dragIndex < 0
    ) {
      setDraggedIndex(null)
      return
    }

    if (onReorder) {
      onReorder(dragIndex, dropIndex)
    }

    setDraggedIndex(null)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
  }

  return (
    <div
      style={{
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Label */}
      <CFormLabel className="mb-2">
        {label}
      </CFormLabel>

      {/* Add Keyword */}
      <CFormInput
        type="text"
        placeholder={`Type and press Enter to add ${label}`}
        value={textInput}
        onChange={handleInputChange}
        onKeyDown={handleKeyPress}
        name="assessment"
      />

      {/* Keywords */}
      <div
        style={{
          marginTop: '10px',
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexWrap: 'wrap',
          alignContent: 'flex-start',
          gap: '5px',
          padding: '4px',
        }}
      >
        {Array.isArray(keywords) &&
          keywords
            .filter((keyword) => keyword?.trim())
            .map((keyword, index) => (
              <CBadge
                key={`${keyword}-${index}`}
                color={
                  editIndex === index
                    ? 'warning'
                    : 'primary'
                }
                draggable
                onDragStart={(event) =>
                  handleDragStart(event, index)
                }
                onDragOver={handleDragOver}
                onDrop={(event) =>
                  handleDrop(event, index)
                }
                onDragEnd={handleDragEnd}
                style={{
                  cursor:
                    draggedIndex === index
                      ? 'grabbing'
                      : 'grab',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '2px',
                  opacity:
                    draggedIndex === index
                      ? 0.5
                      : 1,
                  transition:
                    'opacity 0.15s ease',
                  height: 'fit-content',
                }}
              >
                {/* Drag handle */}
                <GripVertical
                  size={14}
                  strokeWidth={1.5}
                />

                <span>
                  {keyword}
                </span>

                {/* Edit */}
                <span
                  onClick={(event) => {
                    event.stopPropagation()
                    handleEditKeyword(index)
                  }}
                  className="px-1"
                  style={{
                    cursor: 'pointer',
                  }}
                >
                  <EditIcon
                    strokeWidth={1}
                    size={15}
                  />
                </span>

                {/* Delete */}
                <span
                  onClick={(event) => {
                    event.stopPropagation()
                    handleRemoveKeyword(index)
                  }}
                  className="px-1"
                  style={{
                    cursor: 'pointer',
                  }}
                >
                  <X
                    strokeWidth={1}
                    size={15}
                  />
                </span>
              </CBadge>
            ))}
      </div>
    </div>
  )
}

export default RiskFactorDetails